// CSS
import "./TanStackTableFilterPopover.css";

// MUI IMPORTS
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Collapse,
	FormControl,
	FormControlLabel,
	Popover,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";

// MUI ICONS
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// LOCAL CONSTANTS
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const TanStackTableFilterPopover = (props) => {
	// RENDER
	return (
		<Popover
			open={props.open}
			anchorEl={props.anchorEl}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
		>
			<Box id="tanStackTablePopover">
				{props.pendingColumn && (
					<>
						{(() => {
							const currentCol = props.columns.find(
								(col) => col.accessorKey === props.pendingColumn
							);
							const filterType = currentCol?.filterType;

							return (
								<div id="popoverFilterColumnDiv">
									<Typography sx={{ fontWeight: "bold" }}>
										Filter {currentCol.label}
									</Typography>

									{/* Alert/collapse for error message */}
									<Collapse in={!!props.popoverError}>
										<Alert
											severity="error"
											onClose={() =>
												props.setPopoverError("")
											}
										>
											{props.popoverError}
										</Alert>
									</Collapse>

									{/* End alert/collapse for error message */}

									{/* Radio buttons for contains vs n/a */}
									<FormControl>
										<RadioGroup
											value={props.filterMode}
											onChange={(e) => {
												const mode = e.target.value;
												props.setFilterMode(mode);
												if (mode === "does not exist") {
													props.setSortByNotApplicable(
														true
													);
													// If n/a we should clear any inputs
													props.setInputValue(
														filterType ===
															"checkbox"
															? []
															: ""
													);
													props.setDateRange({
														start: null,
														end: null,
													});
												} else {
													props.setSortByNotApplicable(
														false
													);
													props.setInputValue(
														filterType ===
															"checkbox"
															? []
															: ""
													);
													props.setDateRange({
														start: null,
														end: null,
													});
												}
											}}
											sx={{
												mb: 1,
												display: "flex",
												flexDirection: "row",
												gap: "1rem",
											}}
										>
											<FormControlLabel
												value="contains"
												control={<Radio size="small" />}
												label="contains"
											/>
											<FormControlLabel
												value="does not exist"
												control={<Radio size="small" />}
												label="does not exist"
											/>
										</RadioGroup>
									</FormControl>

									{/* Render the appropriate input based on filterType */}
									{filterType === "checkbox" && (
										<Autocomplete
											multiple
											disabled={props.sortByNotApplicable}
											options={currentCol.filterOptions}
											disableCloseOnSelect
											getOptionLabel={(option) =>
												option.label
											}
											value={props.inputValue}
											onChange={(e, newValue) =>
												props.setInputValue(newValue)
											}
											renderOption={(
												props,
												option,
												{ selected }
											) => {
												const { key, ...optionProps } =
													props;
												return (
													<li
														key={key}
														{...optionProps}
													>
														<Checkbox
															icon={icon}
															checkedIcon={
																checkedIcon
															}
															style={{
																marginRight: 8,
															}}
															checked={selected}
														/>
														{option.label}
													</li>
												);
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													label={`Filter ${currentCol.label}`}
												/>
											)}
											fullWidth
										/>
									)}

									{filterType === "textfield" && (
										<TextField
											autoFocus
											disabled={props.sortByNotApplicable}
											label={`Search ${currentCol.label}`}
											// if in “does not exist” mode, show “N/A” here, otherwise show the real value
											value={
												props.sortByNotApplicable
													? "N/A"
													: props.inputValue
											}
											onChange={(e) =>
												props.setInputValue(
													e.target.value
												)
											}
											fullWidth
										/>
									)}

									{filterType === "date" && (
										<LocalizationProvider
											dateAdapter={AdapterDayjs}
										>
											<Box
												sx={{
													display: "flex",
													gap: 1,
												}}
											>
												<DatePicker
													label="Start Date"
													value={
														props.dateRange.start
													}
													onChange={(newValue) =>
														props.setDateRange(
															(prev) => ({
																...prev,
																start: newValue,
															})
														)
													}
													disabled={
														props.sortByNotApplicable
													}
													sx={{ flex: 1 }}
												/>
												<DatePicker
													label="End Date"
													value={props.dateRange.end}
													onChange={(newValue) =>
														props.setDateRange(
															(prev) => ({
																...prev,
																end: newValue,
															})
														)
													}
													disabled={
														props.sortByNotApplicable
													}
													sx={{ flex: 1 }}
												/>
											</Box>
										</LocalizationProvider>
									)}

									{/* Add / Cancel buttons */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											gap: 1,
											mt: 1,
										}}
									>
										<Button
											variant="outlined"
											color="error"
											onClick={props.handlePopoverClose}
										>
											Cancel
										</Button>
										<Button
											variant="contained"
											onClick={
												props.handleAddColumnFilter
											}
										>
											Add
										</Button>
									</Box>
								</div>
							);
						})()}
					</>
				)}
			</Box>
		</Popover>
	);
};

export default TanStackTableFilterPopover;
